// client/src/components/SupportBubble.js
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../AuthContext';
import { firestore } from '../../firebase';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';

const THREAD_COLLECTION = 'supportMessages';

const toMs = (ts) => {
  if (!ts) return 0;
  if (typeof ts === 'number') return ts;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.toDate === 'function') {
    const d = ts.toDate();
    return d instanceof Date ? d.getTime() : 0;
  }
  if (ts instanceof Date) return ts.getTime();
  return 0;
};

const bubbleCss = `
  .sb-root { position: fixed; z-index: 1500; display: flex; flex-direction: column; align-items: flex-end; gap: 12px; font-family: var(--eb-font); }
  .sb-toggle {
    position: relative; display: inline-flex; align-items: center; justify-content: center;
    width: 52px; height: 52px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.08);
    background: var(--eb-brand-800); color: #fff; cursor: pointer;
    box-shadow: 0 10px 24px -6px rgba(15,35,56,0.45), 0 2px 6px rgba(15,35,56,0.18);
    transition: transform .18s var(--eb-ease), background-color .15s ease, box-shadow .15s ease;
  }
  .sb-toggle:hover { background: var(--eb-brand-700); transform: translateY(-1px); }
  .sb-toggle:focus-visible { outline: none; box-shadow: var(--eb-ring), 0 10px 24px -6px rgba(15,35,56,0.45); }
  .sb-toggle svg { width: 22px; height: 22px; }
  .sb-toggle--open { background: var(--eb-text-2); }
  .sb-badge {
    position: absolute; top: -3px; right: -3px; min-width: 20px; height: 20px; padding: 0 5px;
    border-radius: 999px; background: var(--eb-danger); color: #fff; border: 2px solid var(--eb-surface);
    display: inline-flex; align-items: center; justify-content: center; font-size: 11.5px; font-weight: 700; line-height: 1;
  }
  .sb-panel {
    width: 340px; max-width: calc(100vw - 32px);
    background: var(--eb-surface); border: 1px solid var(--eb-border);
    border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-lg);
    overflow: hidden; display: flex; flex-direction: column;
    transform-origin: bottom right; animation: sb-in .2s var(--eb-ease) both;
  }
  @keyframes sb-in { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }
  .sb-header { padding: 16px 18px; background: var(--eb-brand-800); color: #fff; display: flex; align-items: center; gap: 12px; }
  .sb-header__dot { width: 8px; height: 8px; border-radius: 50%; background: #4ade80; box-shadow: 0 0 0 3px rgba(74,222,128,0.2); flex-shrink: 0; }
  .sb-header strong { display: block; font-size: 15px; font-weight: 650; }
  .sb-header p { margin: 2px 0 0; font-size: 13px; color: rgba(255,255,255,0.72); }
  .sb-history {
    max-height: min(40vh, 360px); min-height: 120px; overflow-y: auto; -webkit-overflow-scrolling: touch;
    padding: 16px; background: var(--eb-surface-2);
    display: flex; flex-direction: column; gap: 10px;
  }
  .sb-empty { margin: auto 0; text-align: center; color: var(--eb-muted); font-size: 13.5px; padding: 12px 8px; }
  .sb-msg { max-width: 85%; padding: 10px 12px; border-radius: 14px; font-size: 14px; line-height: 1.45; white-space: pre-wrap; overflow-wrap: anywhere; }
  .sb-msg--user { align-self: flex-end; background: var(--eb-brand); color: #fff; border-bottom-right-radius: 4px; }
  .sb-msg--admin { align-self: flex-start; background: var(--eb-surface); color: var(--eb-text); border: 1px solid var(--eb-border); border-bottom-left-radius: 4px; }
  .sb-time { display: block; margin-top: 4px; font-size: 11.5px; opacity: .7; }
  .sb-footer { padding: 12px; border-top: 1px solid var(--eb-border); background: var(--eb-surface); display: flex; flex-direction: column; gap: 8px; }
  .sb-textarea { width: 100%; min-height: 72px; max-height: 160px; resize: vertical; padding: 10px 12px; font-size: 14.5px; line-height: 1.45; border-radius: var(--eb-radius-sm); }
  .sb-error { color: var(--eb-danger); font-size: 13px; }
  @media (max-width: 767px) {
    .sb-toggle { width: 48px; height: 48px; }
    .sb-toggle svg { width: 20px; height: 20px; }
    .sb-history { max-height: min(36vh, 320px); }
  }
  @media (prefers-reduced-motion: reduce) { .sb-panel { animation: none; } }
`;

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.6A8 8 0 1 1 21 12z" />
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

const formatTimestamp = (ts) => {
  if (!ts) return '';
  try {
    const date = ts.toDate ? ts.toDate() : ts;
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const SupportBubble = () => {
  const { currentUser, userRole } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const openRef = useRef(open);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastReadAt, setLastReadAt] = useState(null);
  const lastOpenTimestampRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  const historyRef = useRef(null);

  const timeline = useMemo(() => {
    if (!Array.isArray(messages)) return [];
    return [...messages].sort((a, b) => toMs(a.createdAt) - toMs(b.createdAt));
  }, [messages]);

  useEffect(() => {
    if (!currentUser) {
      setLastReadAt(null);
      lastOpenTimestampRef.current = null;
      return undefined;
    }

    let cancelled = false;
    const hydrateLastRead = async () => {
      const storageKey =
        userRole === 'admin' ? 'supportBubbleAdminLastOpen' : 'supportBubbleLastOpen';
      const savedTimestamp = (() => {
        try {
          const raw = localStorage.getItem(storageKey);
          return raw ? new Date(parseInt(raw, 10)) : null;
        } catch {
          return null;
        }
      })();

      if (userRole === 'admin') {
        if (!cancelled) {
          setLastReadAt(savedTimestamp);
          lastOpenTimestampRef.current = savedTimestamp;
        }
        return;
      }

      try {
        const threadRef = doc(firestore, THREAD_COLLECTION, currentUser.uid);
        const snap = await getDoc(threadRef);
        const serverTs = snap.exists() ? snap.data().userLastReadAt : null;
        const effective = serverTs || savedTimestamp || null;
        if (!cancelled) {
          setLastReadAt(effective);
          lastOpenTimestampRef.current = effective;
        }
      } catch (err) {
        console.error('Support bubble last read fetch error:', err);
        if (!cancelled) {
          setLastReadAt(savedTimestamp || null);
          lastOpenTimestampRef.current = savedTimestamp || null;
        }
      }
    };

    hydrateLastRead();

    return () => {
      cancelled = true;
    };
  }, [currentUser, userRole]);

  useEffect(() => {
    openRef.current = open;
    if (open && userRole !== 'admin') {
      setUnreadCount(0);
      const now = new Date();
      lastOpenTimestampRef.current = now;
      setLastReadAt(now);
      try {
        localStorage.setItem('supportBubbleLastOpen', now.getTime().toString());
      } catch {}

      if (currentUser) {
        const threadRef = doc(firestore, THREAD_COLLECTION, currentUser.uid);
        const baseMetadata = {
          userId: currentUser.uid,
          userEmail: currentUser.email || '',
          userRole: userRole || 'client',
          displayName: currentUser.displayName || currentUser.email || currentUser.uid,
          userLastReadAt: serverTimestamp(),
        };
        (async () => {
          try {
            await setDoc(threadRef, baseMetadata, { merge: true });
          } catch (err) {
            console.error('Support bubble mark read error:', err);
          }
        })();
      }
    }
  }, [currentUser, open, userRole]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setMessages([]);
      setUnreadCount(0);
      setLastReadAt(null);
      return () => {};
    }

    let unsubscribe;

    if (userRole === 'admin') {
      const q = query(collection(firestore, THREAD_COLLECTION));
      unsubscribe = onSnapshot(q, (snapshot) => {
        const threads = snapshot.docs.map((d) => d.data());
        const newThreads = threads.filter(
          (thread) =>
            thread.lastMessageSender === 'user' &&
            thread.lastMessageAt &&
            (lastOpenTimestampRef.current === null ||
              thread.lastMessageAt.toDate() > lastOpenTimestampRef.current),
        );
        setUnreadCount(newThreads.length);
      });
    } else {
      const messagesRef = collection(firestore, THREAD_COLLECTION, currentUser.uid, 'messages');
      const q = query(messagesRef, orderBy('createdAt', 'asc'));

      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const data = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
          setMessages(data);
        },
        (err) => {
          console.error('Support bubble snapshot error:', err);
          setError('Impossible de recuperer vos messages pour le moment.');
        },
      );
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser, userRole]);

  useEffect(() => {
    if (userRole === 'admin') return;
    if (openRef.current) {
      setUnreadCount(0);
      return;
    }

    const lastReadMs = toMs(lastOpenTimestampRef.current);
    const newAdminMessages = messages.filter(
      (msg) => msg.sender === 'admin' && toMs(msg.createdAt) > lastReadMs,
    );
    setUnreadCount(newAdminMessages.length);
  }, [messages, userRole, lastReadAt]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [open]);

  useEffect(() => {
    if (open && historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight;
    }
  }, [open, timeline]);

  const sendMessage = async (evt) => {
    evt.preventDefault();
    if (!currentUser || !message.trim()) return;

    setSending(true);
    setError('');
    try {
      const threadRef = doc(firestore, THREAD_COLLECTION, currentUser.uid);
      const threadSnap = await getDoc(threadRef);
      const baseMetadata = {
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        userRole: userRole || 'client',
        displayName: currentUser.displayName || currentUser.email || currentUser.uid,
      };
      if (!threadSnap.exists()) {
        baseMetadata.createdAt = serverTimestamp();
      }
      await setDoc(threadRef, baseMetadata, { merge: true });

      await addDoc(collection(threadRef, 'messages'), {
        sender: 'user',
        content: message.trim(),
        createdAt: serverTimestamp(),
      });

      await updateDoc(threadRef, {
        hasUnansweredUserMessage: true,
        lastMessageAt: serverTimestamp(),
        lastMessageSender: 'user',
        lastMessagePreview: message.trim().slice(0, 120),
      });
      setMessage('');
    } catch (err) {
      console.error('Send support message error:', err);
      setError("Echec de l'envoi du message, reessaie dans un instant.");
    } finally {
      setSending(false);
    }
  };

  const handleAdminClick = () => {
    const now = new Date();
    lastOpenTimestampRef.current = now;
    localStorage.setItem('supportBubbleAdminLastOpen', now.getTime().toString());
    setUnreadCount(0);
    navigate('/adminsupport');
  };

  if (!currentUser) {
    return null;
  }

  // Mobile : au-dessus de la barre d'onglets (72px) + zone sûre iOS
  const containerPosition = isMobile
    ? { bottom: 'calc(var(--eb-bottom-nav-h) + 16px + env(safe-area-inset-bottom, 0px))', right: '16px' }
    : { bottom: '24px', right: '24px' };

  if (userRole === 'admin') {
    return (
      <div className="sb-root" style={containerPosition}>
        <style>{bubbleCss}</style>
        <button
          aria-label="Contacter le support"
          title="Support"
          className="sb-toggle"
          onClick={handleAdminClick}
        >
          {unreadCount > 0 && <span className="sb-badge">{unreadCount}</span>}
          <ChatIcon />
        </button>
      </div>
    );
  }

  return (
    <div className="sb-root" style={containerPosition}>
      <style>{bubbleCss}</style>
      {open && (
        <div className="sb-panel" role="dialog" aria-label="Support client">
          <div className="sb-header">
            <span className="sb-header__dot" aria-hidden="true" />
            <div>
              <strong>Support client</strong>
              <p>Ecris-nous, nous repondons rapidement.</p>
            </div>
          </div>

          <div className="sb-history" ref={historyRef}>
            {timeline.length === 0 ? (
              <div className="sb-empty">
                Aucun message pour le moment. Lance la conversation !
              </div>
            ) : (
              timeline.map((item) => (
                <div
                  key={item.id}
                  className={`sb-msg ${item.sender === 'admin' ? 'sb-msg--admin' : 'sb-msg--user'}`}
                >
                  {item.content}
                  <span className="sb-time">{formatTimestamp(item.createdAt)}</span>
                </div>
              ))
            )}
          </div>

          <form className="sb-footer" onSubmit={sendMessage}>
            <textarea
              className="sb-textarea"
              placeholder="Explique ton besoin..."
              value={message}
              disabled={sending}
              onChange={(e) => setMessage(e.target.value)}
            />
            <button
              type="submit"
              className="eb-btn eb-btn--primary eb-btn--block"
              disabled={sending || !message.trim()}
            >
              {sending ? 'Envoi...' : 'Envoyer'}
            </button>
            {error && <div className="sb-error">{error}</div>}
          </form>
        </div>
      )}

      <button
        aria-label="Contacter le support"
        aria-expanded={open}
        className={`sb-toggle${open ? ' sb-toggle--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
      >
        {unreadCount > 0 && !open && <span className="sb-badge">{unreadCount}</span>}
        {open ? <CloseIcon /> : <ChatIcon />}
      </button>
    </div>
  );
};

export default SupportBubble;
